// 권위: docs/ARCH_DASHBOARD.md §4.11 + §6.2 (REST 14종)
//       docs/proposals/SectorIntelligence_v2.md v0.3 §2.7
//
// REST 엔드포인트 (화면 11 신설):
//   정규 섹터 (sector_taxonomy):
//     GET    /api/v1/sectors/taxonomy
//     POST   /api/v1/sectors/taxonomy
//     GET    /api/v1/sectors/taxonomy/{canonical_sector_id}
//     PATCH  /api/v1/sectors/taxonomy/{canonical_sector_id}
//
//   매핑 (sector_mapping, 1:N 허용):
//     GET    /api/v1/sectors/taxonomy/mappings
//     POST   /api/v1/sectors/taxonomy/mappings
//     PATCH  /api/v1/sectors/taxonomy/mappings/{mapping_id}
//     POST   /api/v1/sectors/taxonomy/mappings/{mapping_id}/approve
//     POST   /api/v1/sectors/taxonomy/mappings/{mapping_id}/reject
//     POST   /api/v1/sectors/taxonomy/mappings/bulk-approve
//
//   Type-A 실행:
//     POST   /api/v1/sectors/taxonomy/rebalance
//     GET    /api/v1/sectors/taxonomy/rebalance/runs
//     GET    /api/v1/sectors/taxonomy/rebalance/runs/{run_id}
//
//   밸런싱 지표 (sector_balance_stats):
//     GET    /api/v1/sectors/taxonomy/balance-stats
//
//   감사 이력:
//     GET    /api/v1/sectors/taxonomy/audit
//
// envelope 언랩은 apiClient interceptor 가 자동 처리. 본 모듈은 data 만 다룬다.

import { apiGet, apiPost, apiPatch } from './client';

// ── 타입 정의 ──────────────────────────────────────────────────────

export type TaxonomySource = 'naver_primary' | 'naver_theme';
export type MappingStatus = 'confirmed' | 'pending_review' | 'rejected';

export interface SectorTaxonomyNode {
  canonicalSectorId: string;
  displayName: string;
  parentSectorId: string | null;
  description: string | null;
  isActive: boolean;
  symbolCount: number;
  /** confirmed 매핑 수 (하위호환). 아래 confirmedMappingCount 와 동일값. */
  mappingCount: number;
  pendingMappingCount?: number;
  confirmedMappingCount?: number;
  createdAt?: string;
  updatedAt: string;
}

export interface SectorMappingItem {
  mappingId: number;
  source: TaxonomySource;
  sourceSectorName: string;
  canonicalSectorId: string;
  canonicalDisplayName: string;
  status: MappingStatus;
  weight: number;
  mappingReason: string | null;
  llmConfidence: number | null;
  llmModel: string | null;
  reviewer: string | null;
  reviewedAt: string | null;
  reviewNote: string | null;
  createdAt: string;
  updatedAt?: string;
  /** BootstrapProposal v2.0 merge_strategy — rename|merge|split|new. null 가능. */
  mergeStrategy: string | null;
  /** BootstrapProposal v2.0 mapping.relation — primary_name|sibling_by_overlap|... null 가능. */
  relation: string | null;
  /** sibling_by_overlap 일 때 Jaccard 값 (0~1). 그 외 null. */
  overlapRatio: number | null;
}

export interface SectorMappingListResponse {
  items: SectorMappingItem[];
  total: number;
}

export interface SectorBalanceStatsItem {
  statDate: string;                    // "YYYY-MM-DD"
  totalCanonicalSectors: number;
  totalSourceSectors: number;
  consolidationRatio: number;          // canonical / source
  avgSymbolsPerCanonical: number;
  stddevSymbolsPerCanonical: number;
  canonicalOverlapScore: number;       // SUM(Jaccard) 정규화
  unmappedSourceCount: number;
  newSourceSectors30d: number;
  // ── 중복(overlap) 지표 5종 — §4.11 확장 (2026-04) ─────────────
  // 백엔드(dashboard-api)에서 camelCase 로 내려주며, null 가능 (집계 실패 시).
  // 해석 가이드는 BalanceChartsTab 의 도움말 패널 참조.
  highOverlapPairCount70?: number | null;   // Jaccard ≥ 0.70 쌍 수 (극심 중복)
  highOverlapPairCount50?: number | null;   // Jaccard ≥ 0.50 쌍 수 (중복 의심)
  topOverlapMax?: number | null;            // 최대 Jaccard 값 (0~1)
  top10OverlapAvg?: number | null;          // 상위 10쌍 Jaccard 평균 (0~1)
  weightedOverlapScore?: number | null;     // Σ(jaccard × |교집합|) / Σ|교집합| (0~1)
}

export interface RebalanceRunSummary {
  runId: string;
  triggerBy: string;
  startedAt: string;
  completedAt: string | null;
  status: 'running' | 'completed' | 'failed';
  costUsd: number | null;
  proposedCount: number | null;
  uncoveredCount: number | null;
  llmModel: string | null;
}

export interface RebalanceRunDetail extends RebalanceRunSummary {
  proposedTaxonomy: unknown;  // JSON blob (구조는 백엔드가 권위)
  uncoveredSources: string[];
  durationSeconds: number | null;
}

/**
 * 감사 이력 엔트리. 백엔드 DTO `SectorAuditEntry` 와 정합.
 *
 * MVP 단계에서는 상태 전이(from→to) 대신 최종 `status` 만 기록한다.
 * `updatedAt` 이 감사 시점. 안정 key 는 `mappingId + updatedAt` 조합.
 */
export interface TaxonomyAuditEntry {
  mappingId: number;
  source: TaxonomySource | null;
  sourceSectorName: string | null;
  canonicalSectorId: string | null;
  canonicalDisplayName: string | null;
  status: MappingStatus;
  weight: number | null;
  reviewer: string | null;
  reviewedAt: string | null;
  reviewNote: string | null;
  updatedAt: string;
}

export interface TaxonomyAuditListResponse {
  items: TaxonomyAuditEntry[];
  total: number;
}

// ── 피드백 (Type-A LLM BootstrapProposal v2.0) ────────────────────
// 권위: Type-A BootstrapProposal v2.0 (커밋 d5eff54) `feedback[]` 채널.
// dashboard-api 측 엔드포인트: GET /api/v1/sectors/taxonomy/feedback
// 하나의 run 이 다수의 feedback 엔트리를 낳을 수 있으며, 본 응답은 플랫 리스트.

export type TaxonomyFeedbackKind =
  | 'question'
  | 'improvement_request'
  | 'data_issue'
  | 'ambiguity_flag';

export type TaxonomyFeedbackSeverity = 'info' | 'suggestion' | 'blocker';

export type TaxonomyFeedbackScope =
  | 'input_data'
  | 'prompt'
  | 'output_schema'
  | 'specific_sector';

export interface TaxonomyFeedbackItem {
  runId: string;
  runCompletedAt: string;             // ISO8601
  runTriggerBy: string;               // email 또는 user id
  kind: TaxonomyFeedbackKind | string;
  severity: TaxonomyFeedbackSeverity | string;
  scope: TaxonomyFeedbackScope | string;
  /**
   * 참조 대상 — 3가지 모양:
   *   - "sec_xxx"                  → 정규 섹터 슬러그 (택소노미 탭 딥링크)
   *   - "naver_primary:ABC"        → 원천 섹터 (매핑 탭 필터)
   *   - "naver_theme:XYZ"          → 원천 섹터 (매핑 탭 필터)
   *   - null                       → 일반 코멘트 (딥링크 없음)
   */
  targetRef: string | null;
  message: string;
  feedbackIndex: number;              // run 내부 순번 (안정적 key 보조)
}

export interface TaxonomyFeedbackRunMeta {
  runId: string;
  completedAt: string;
  feedbackCount: number;
  blockerCount: number;
}

export interface TaxonomyFeedbackListResponse {
  items: TaxonomyFeedbackItem[];
  total: number;
  runs: TaxonomyFeedbackRunMeta[];
}

export interface FeedbackListQuery {
  runId?: string;
  kind?: TaxonomyFeedbackKind[];
  severity?: TaxonomyFeedbackSeverity[];
  scope?: TaxonomyFeedbackScope[];
  from?: string;                      // "YYYY-MM-DD"
  to?: string;                        // "YYYY-MM-DD"
  page: number;
  pageSize: number;
}

// ── 쿼리 파라미터 ──────────────────────────────────────────────────

export interface MappingListQuery {
  source?: TaxonomySource[];
  sourceSectorName?: string;
  canonicalSectorId?: string;
  /** canonical_sector_id LIKE '{prefix}%' — fallback(src_*) 탐색용. */
  canonicalPrefix?: string;
  status?: MappingStatus[];
  minConfidence?: number;
  from?: string;
  to?: string;
  page: number;
  pageSize: number;
}

export interface AuditListQuery {
  mappingId?: number;
  reviewer?: string;
  from?: string;
  to?: string;
  statusTransition?: string; // 예: "pending_review->confirmed"
  page: number;
  pageSize: number;
}

export interface BalanceStatsQuery {
  from: string;
  to: string;
  metrics?: string[];
}

// ── API 호출 ──────────────────────────────────────────────────────

const BASE = '/api/v1/sectors/taxonomy';

export async function listTaxonomy(params?: {
  isActive?: boolean;
  parent?: string;
  search?: string;
}): Promise<SectorTaxonomyNode[]> {
  const p = new URLSearchParams();
  if (params?.isActive !== undefined) p.set('is_active', String(params.isActive));
  if (params?.parent) p.set('parent', params.parent);
  if (params?.search?.trim()) p.set('search', params.search.trim());
  const qs = p.toString();
  return apiGet<SectorTaxonomyNode[]>(`${BASE}${qs ? `?${qs}` : ''}`);
}

export async function createTaxonomy(body: {
  canonicalSectorId: string;
  displayName: string;
  parentSectorId?: string | null;
  description?: string | null;
}): Promise<SectorTaxonomyNode> {
  return apiPost<SectorTaxonomyNode>(BASE, body);
}

export async function getTaxonomyDetail(
  canonicalSectorId: string
): Promise<SectorTaxonomyNode & { mappings: SectorMappingItem[] }> {
  return apiGet(`${BASE}/${encodeURIComponent(canonicalSectorId)}`);
}

export async function patchTaxonomy(
  canonicalSectorId: string,
  body: Partial<{
    displayName: string;
    description: string | null;
    isActive: boolean;
    parentSectorId: string | null;
  }>
): Promise<SectorTaxonomyNode> {
  return apiPatch<SectorTaxonomyNode>(
    `${BASE}/${encodeURIComponent(canonicalSectorId)}`,
    body
  );
}

// ── 매핑 ─────────────────────────────────────────────────────────

function buildMappingQuery(q: MappingListQuery): URLSearchParams {
  const p = new URLSearchParams();
  p.set('page', String(Math.max(0, q.page)));
  p.set('page_size', String(Math.max(1, Math.min(500, q.pageSize))));
  if (q.source && q.source.length > 0) p.set('source', q.source.join(','));
  if (q.sourceSectorName?.trim()) p.set('source_sector_name', q.sourceSectorName.trim());
  if (q.canonicalSectorId?.trim())
    p.set('canonical_sector_id', q.canonicalSectorId.trim());
  if (q.canonicalPrefix?.trim())
    p.set('canonical_prefix', q.canonicalPrefix.trim());
  if (q.status && q.status.length > 0) p.set('status', q.status.join(','));
  if (q.minConfidence !== undefined && q.minConfidence > 0)
    p.set('min_confidence', q.minConfidence.toFixed(2));
  if (q.from) p.set('from', q.from);
  if (q.to) p.set('to', q.to);
  return p;
}

export async function listMappings(
  query: MappingListQuery
): Promise<SectorMappingListResponse> {
  const qs = buildMappingQuery(query).toString();
  return apiGet<SectorMappingListResponse>(`${BASE}/mappings?${qs}`);
}

export async function createMapping(body: {
  source: TaxonomySource;
  sourceSectorName: string;
  canonicalSectorId: string;
  weight?: number;
  status: MappingStatus;
  mappingReason?: string | null;
  llmConfidence?: number | null;
}): Promise<SectorMappingItem> {
  return apiPost<SectorMappingItem>(`${BASE}/mappings`, body);
}

export async function patchMapping(
  mappingId: number,
  body: Partial<{
    weight: number;
    canonicalSectorId: string;
    reviewNote: string | null;
  }>
): Promise<SectorMappingItem> {
  return apiPatch<SectorMappingItem>(`${BASE}/mappings/${mappingId}`, body);
}

export async function approveMapping(
  mappingId: number,
  body?: { approver?: string; note?: string | null }
): Promise<void> {
  await apiPost(`${BASE}/mappings/${mappingId}/approve`, body ?? {});
}

export async function rejectMapping(
  mappingId: number,
  reason: string,
  approver?: string
): Promise<void> {
  await apiPost(`${BASE}/mappings/${mappingId}/reject`, {
    approver,
    reason,
  });
}

export interface BulkApproveFilter {
  minConfidence?: number;
  status?: MappingStatus;
}

export interface BulkApproveBody {
  mappingIds?: number[];
  filter?: BulkApproveFilter;
}

/**
 * 백엔드 BulkApproveResponse = { requested, succeeded, failed, errors[] }.
 * bulk-reject 는 의도적으로 동일 shape 재사용 (succeeded 는 처리된 건수).
 */
export interface BulkActionError {
  mappingId: number;
  code: string;
  message: string;
}

export interface BulkActionResponse {
  requested: number;
  succeeded: number;
  failed: number;
  errors: BulkActionError[];
}

export async function bulkApproveMappings(
  body: BulkApproveBody
): Promise<BulkActionResponse> {
  return apiPost<BulkActionResponse>(`${BASE}/mappings/bulk-approve`, body);
}

// 매핑 일괄 반려 — 권위: dashboard-api bulk-reject 엔드포인트.
// mappingIds 또는 filter 중 하나 + reason 필수. 응답은 BulkActionResponse 재사용.
export interface BulkRejectBody {
  mappingIds?: number[];
  filter?: BulkApproveFilter;
  reason: string;
}

export async function bulkRejectMappings(
  body: BulkRejectBody
): Promise<BulkActionResponse> {
  return apiPost<BulkActionResponse>(`${BASE}/mappings/bulk-reject`, body);
}

// ── 전체 매핑 reset (운영자 결정 2026-05-18) ───────────────────
// 권위: dashboard-api SectorMappingController.resetAllMappings (예정).
//
// 동작:
//   - 모든 sector_mapping 행을 삭제 (confirmed / pending_review / rejected / superseded).
//   - 운영자가 LLM Type-A 를 재실행하여 새 매핑을 다시 생성하는 워크플로 진입점.
//   - 비가역 작업이므로 클라이언트에서 "RESET" 타이핑 게이트로 보호.
//
// 응답:
//   - 200: { mappingCountBefore, eventId, occurredAt }

export interface ResetMappingsBody {
  resetReason: string;
}

export interface ResetMappingsResponse {
  mappingCountBefore: number;
  eventId: string;
  occurredAt: string;
}

export async function resetMappings(
  body: ResetMappingsBody,
): Promise<ResetMappingsResponse> {
  return apiPost<ResetMappingsResponse>(`${BASE}/mappings/reset`, body);
}

// ── Type-A 실행 ──────────────────────────────────────────────────

export interface TriggerRebalanceResponse {
  runId: string;
  status: string;              // "queued"
  estimatedCostUsd: number;
  queuedAt: string;
  triggerBy: string;
}

/**
 * Type-A (부트스트래핑·전체 리밸런싱) 실행 요청.
 * 백엔드 SectorTaxonomyDtos.RebalanceRequest 는 `triggerBy` (NotBlank) + `confirmCostUsd` (NotNull) 요구.
 * `triggerBy` 는 운영자 식별자 (email 또는 user id). 2단계 확인은 클라이언트 측 처리.
 */
export async function triggerRebalance(
  triggerBy: string,
  confirmCostUsd: number = 0.17,
): Promise<TriggerRebalanceResponse> {
  return apiPost<TriggerRebalanceResponse>(`${BASE}/rebalance`, {
    triggerBy,
    confirmCostUsd,
  });
}

export async function listRebalanceRuns(params?: {
  limit?: number;
  status?: RebalanceRunSummary['status'];
}): Promise<RebalanceRunSummary[]> {
  const p = new URLSearchParams();
  if (params?.limit) p.set('limit', String(params.limit));
  if (params?.status) p.set('status', params.status);
  const qs = p.toString();
  return apiGet<RebalanceRunSummary[]>(
    `${BASE}/rebalance/runs${qs ? `?${qs}` : ''}`
  );
}

export async function getRebalanceRun(runId: string): Promise<RebalanceRunDetail> {
  return apiGet<RebalanceRunDetail>(
    `${BASE}/rebalance/runs/${encodeURIComponent(runId)}`
  );
}

// ── 밸런싱 지표 ──────────────────────────────────────────────────

export async function listBalanceStats(
  from: string,
  to: string,
  metrics?: string[]
): Promise<SectorBalanceStatsItem[]> {
  const p = new URLSearchParams();
  p.set('from', from);
  p.set('to', to);
  if (metrics && metrics.length > 0) p.set('metrics', metrics.join(','));
  return apiGet<SectorBalanceStatsItem[]>(`${BASE}/balance-stats?${p.toString()}`);
}

// ── 감사 이력 ────────────────────────────────────────────────────

function buildAuditQuery(q: AuditListQuery): URLSearchParams {
  const p = new URLSearchParams();
  p.set('page', String(Math.max(0, q.page)));
  p.set('page_size', String(Math.max(1, Math.min(500, q.pageSize))));
  if (q.mappingId !== undefined) p.set('mapping_id', String(q.mappingId));
  if (q.reviewer?.trim()) p.set('reviewer', q.reviewer.trim());
  if (q.from) p.set('from', q.from);
  if (q.to) p.set('to', q.to);
  if (q.statusTransition) p.set('status_transition', q.statusTransition);
  return p;
}

export async function listAudit(
  query: AuditListQuery
): Promise<TaxonomyAuditListResponse> {
  const qs = buildAuditQuery(query).toString();
  return apiGet<TaxonomyAuditListResponse>(`${BASE}/audit?${qs}`);
}

// ── 피드백 ───────────────────────────────────────────────────────

function buildFeedbackQuery(q: FeedbackListQuery): URLSearchParams {
  // 백엔드 SectorFeedbackController 는 camelCase 파라미터 (runId / pageSize) 를
  // 기대한다. 과거 run_id / page_size 스네이크케이스로 전송하면 필터가 먹지 않음.
  const p = new URLSearchParams();
  p.set('page', String(Math.max(0, q.page)));
  p.set('pageSize', String(Math.max(1, Math.min(500, q.pageSize))));
  if (q.runId?.trim()) p.set('runId', q.runId.trim());
  if (q.kind && q.kind.length > 0) p.set('kind', q.kind.join(','));
  if (q.severity && q.severity.length > 0) p.set('severity', q.severity.join(','));
  if (q.scope && q.scope.length > 0) p.set('scope', q.scope.join(','));
  if (q.from) p.set('from', q.from);
  if (q.to) p.set('to', q.to);
  return p;
}

export async function listFeedback(
  query: FeedbackListQuery
): Promise<TaxonomyFeedbackListResponse> {
  const qs = buildFeedbackQuery(query).toString();
  return apiGet<TaxonomyFeedbackListResponse>(`${BASE}/feedback?${qs}`);
}

// ── 머지 / 해제 (Sprint 1 신규) ──────────────────────────────────
// 권위: ARCH_DASHBOARD.md v1.7 §4.11 운영자 머지/해제 액션.
//
// mergeMappings:
//   운영자가 리뷰 큐에서 다수 mapping 을 선택해 단일 canonical 섹터로 통합.
//   백엔드는 target 아닌 원소들을 rejected 로 마킹 + audit 기록, target 은 confirmed 로.
//
// unmergeMapping:
//   confirmed mapping 하나를 되돌려 "pending_review" 로 복귀 (또는 rejected 로 소프트 제거).
//   마지막 confirmed 매핑일 경우 target 섹터가 orphan 상태가 되어 422 "would_orphan"
//   반환 — 운영자가 force=true 로 재시도하면 강제 진행.

export interface MergeRequest {
  mappingIds: number[];
  targetCanonicalSectorId: string;
  approver?: string;
  note?: string;
}

export interface MergeResponse {
  mergedCount: number;
  targetCanonical: { id: string; displayName: string };
  affected: number[];
}

export async function mergeMappings(req: MergeRequest): Promise<MergeResponse> {
  return apiPost<MergeResponse>(`${BASE}/mappings/merge`, req);
}

export interface UnmergeRequest {
  approver?: string;
  reason: string;
  force?: boolean;
}

export interface UnmergeResponse {
  mappingId: number;
  prevCanonicalSectorId: string;
  status: 'rejected';
}

export async function unmergeMapping(
  mappingId: number,
  req: UnmergeRequest
): Promise<UnmergeResponse> {
  return apiPost<UnmergeResponse>(`${BASE}/mappings/${mappingId}/unmerge`, req);
}

// ── 일괄 머지 (운영자 결정 2026-05-15) ─────────────────────────
// 권위: dashboard-api SectorMappingController.bulkMerge.
//
// 동작:
//   - mappingIds[] 각각을 targetCanonical 로 통합 (server-side mergeInto 반복).
//   - 정책 3 (uncovered coverage) 위반 시 422 + 트랜잭션 롤백.
//   - target 이 sec_l1_meta_* prefix 면 400 invalid_target.
//
// 응답:
//   - 200: { mergedCount, targetCanonical, skippedCount }
//   - 400 invalid_target / mapping_ids_empty / target_canonical_missing
//   - 422 PromotionConsistencyException — ApiError.meta 에 uncovered_symbols_sample
//         (LinkedHashMap → JSON object) 등을 포함.

export interface BulkMergeRequest {
  mappingIds: number[];
  targetCanonical: string;
  reviewer?: string;
  note?: string;
}

export interface BulkMergeResponse {
  mergedCount: number;
  targetCanonical: string;
  skippedCount: number;
}

export async function bulkMergeMappings(
  body: BulkMergeRequest
): Promise<BulkMergeResponse> {
  return apiPost<BulkMergeResponse>(`${BASE}/mappings/bulk-merge`, body);
}

// ── 섹터 유사도 (탭 F) ─────────────────────────────────────────
// 권위: dashboard-api v1.6 GET /api/v1/sectors/taxonomy/similarity.
//       docs/ARCH_DATA.md §5.16 sector_similarity — scope 컬럼은 source | canonical.
// 응답은 envelope unwrap 후 SectorSimilarityPair[] (쌍 리스트). 매트릭스 축은
// 클라이언트에서 pairs 를 순회해 조립한다. PRIMARY/SECONDARY/THEME 차원은 백엔드
// 미지원 (별도 기능).

export type SimilarityScope = 'source' | 'canonical';

export interface SectorSimilarityPair {
  /** 원천 섹터명(source scope) 또는 canonical_sector_id(canonical scope). */
  sectorA: string;
  sectorB: string;
  jaccardSimilarity: number;          // 0 ~ 1
  intersectionSize: number;
  unionSize: number;
  computedAt: string;                 // ISO8601 또는 "YYYY-MM-DD"
  scope: SimilarityScope;
}

export interface SimilarityQuery {
  /** 기본 source. */
  scope?: SimilarityScope;
  top?: number;
  /** 특정 섹터 drill-down — 이 섹터가 포함된 쌍만 필터링. */
  sector?: string;
  /** ISO8601 "YYYY-MM-DD" — 과거 스냅샷 조회 시. */
  computedAt?: string;
}

export async function listTopSimilarity(
  query: SimilarityQuery = {}
): Promise<SectorSimilarityPair[]> {
  const p = new URLSearchParams();
  if (query.scope) p.set('scope', query.scope);
  if (query.top !== undefined) p.set('top', String(query.top));
  if (query.sector?.trim()) p.set('sector', query.sector.trim());
  if (query.computedAt) p.set('computed_at', query.computedAt);
  const qs = p.toString();
  return apiGet<SectorSimilarityPair[]>(
    `${BASE}/similarity${qs ? `?${qs}` : ''}`
  );
}
